import React from 'react';

interface IconProps {
  size?: number;
  color?: string;
  className?: string;
}

/** Assault / generic rifle — side-profile silhouette */
export const RifleIcon: React.FC<IconProps> = ({ size = 28, color = 'currentColor', className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <path d="M2 11h4l1-2h8l1 2h6v2h-6l-1-1H8v1H6l-1 3H3l1-3H2v-2z" fill={color} />
    <rect x="10" y="7" width="1.5" height="3" rx={0.5} fill={color} opacity={0.7} />
    <path d="M9 14l-1 4.5h2L9 14z" fill={color} opacity={0.8} />
  </svg>
);

/** Pistol — compact handgun profile */
export const PistolIcon: React.FC<IconProps> = ({ size = 28, color = 'currentColor', className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <path d="M6 10h10l2-1h2v3H6v-2z" fill={color} />
    <path d="M8 13l-1 6h3l-1-4h4v-2H8z" fill={color} opacity={0.85} />
    <rect x="18" y="9.5" width="2" height="1" rx={0.3} fill={color} opacity={0.5} />
  </svg>
);

/** Shotgun — long barrel, pump grip */
export const ShotgunIcon: React.FC<IconProps> = ({ size = 28, color = 'currentColor', className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <path d="M1 11h5l1-1h9l1 1h5v2H1v-2z" fill={color} />
    <rect x="6" y="13" width="4" height="1.5" rx={0.4} fill={color} opacity={0.6} />
    <path d="M12 13l-1 5h2l-1-5z" fill={color} opacity={0.8} />
  </svg>
);

/** SMG / LMG — compact body with extended magazine */
export const SmgIcon: React.FC<IconProps> = ({ size = 28, color = 'currentColor', className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <path d="M3 10h4l1-1h7l1 1h5v2H3v-2z" fill={color} />
    <rect x="11" y="12" width="2.5" height="5" rx={0.6} fill={color} opacity={0.8} />
    <path d="M7 12l-1 4h2l-1-4z" fill={color} opacity={0.7} />
  </svg>
);

/** Sniper — extra-long barrel with scope */
export const SniperIcon: React.FC<IconProps> = ({ size = 28, color = 'currentColor', className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <path d="M1 12h6l1-2h10l1 1h4v2H1v-1z" fill={color} />
    <rect x="10" y="7.5" width="4" height="2.5" rx={1} stroke={color} strokeWidth={1} fill="none" />
    <line x1="12" y1="10" x2="12" y2="12" stroke={color} strokeWidth={0.8} />
    <path d="M13 14l-1 5h2l-1-5z" fill={color} opacity={0.8} />
  </svg>
);

/** Ammo / Magazine — bullets in a box mag */
export const AmmoIcon: React.FC<IconProps> = ({ size = 28, color = 'currentColor', className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <rect x="7" y="4" width="10" height="16" rx={1.5} stroke={color} strokeWidth={1.4} fill={color} fillOpacity={0.15} />
    <path d="M9 8h6M9 11h6M9 14h6M9 17h6" stroke={color} strokeWidth={1} strokeLinecap="round" opacity={0.7} />
    <path d="M10 4V2.5h4V4" stroke={color} strokeWidth={1} strokeLinecap="round" />
  </svg>
);

/** Helmet — rounded dome with visor slit */
export const HelmetIcon: React.FC<IconProps> = ({ size = 28, color = 'currentColor', className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <path d="M6 14a6 6 0 0 1 12 0" fill={color} fillOpacity={0.25} stroke={color} strokeWidth={1.4} />
    <path d="M5 14h14v3c0 1.5-1.5 2.5-3.5 2.5h-7C6.5 19.5 5 18.5 5 17v-3z" fill={color} fillOpacity={0.4} stroke={color} strokeWidth={1.2} />
    <rect x="7" y="14.5" width="10" height="2" rx={0.8} fill={color} fillOpacity={0.7} />
  </svg>
);

/** Chest armor — body armor vest front view */
export const ChestArmorIcon: React.FC<IconProps> = ({ size = 28, color = 'currentColor', className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <path d="M7 5L4 8v9l2 2h12l2-2V8l-3-3h-3l-2 2-2-2H7z" fill={color} fillOpacity={0.3} stroke={color} strokeWidth={1.3} strokeLinejoin="round" />
    <path d="M10 7l2 2.5L14 7" stroke={color} strokeWidth={1} strokeLinecap="round" strokeLinejoin="round" />
    <path d="M8 12h8M9 15h6" stroke={color} strokeWidth={0.8} opacity={0.5} strokeLinecap="round" />
  </svg>
);

/** Arm armor — forearm bracer/gauntlet */
export const ArmArmorIcon: React.FC<IconProps> = ({ size = 28, color = 'currentColor', className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <path d="M6 7c0-.6.4-1 1-1h10c.6 0 1 .4 1 1v10c0 .6-.4 1-1 1H7c-.6 0-1-.4-1-1V7z" fill={color} fillOpacity={0.2} stroke={color} strokeWidth={1.3} />
    <path d="M8 10h8M8 13h8M8 16h8" stroke={color} strokeWidth={0.9} strokeLinecap="round" opacity={0.6} />
    <path d="M10 6V4.5M14 6V4.5" stroke={color} strokeWidth={1} strokeLinecap="round" />
  </svg>
);

/** Leg armor — greave/shin guard */
export const LegArmorIcon: React.FC<IconProps> = ({ size = 28, color = 'currentColor', className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <path d="M9 3h6l.5 11-1.5 3h1.5v3H8.5v-3H10l-1.5-3L9 3z" fill={color} fillOpacity={0.25} stroke={color} strokeWidth={1.3} strokeLinejoin="round" />
    <path d="M10 7h4M10 10h4M10.5 13h3" stroke={color} strokeWidth={0.8} strokeLinecap="round" opacity={0.6} />
  </svg>
);

/** Dismantle — wrench + break-apart arrows */
export const DismantleIcon: React.FC<IconProps> = ({ size = 28, color = 'currentColor', className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <path d="M14.7 3.3a4 4 0 0 1 .8 4.5l-6.2 6.2a4 4 0 0 1-4.5-.8 4 4 0 0 1-.8-4.5l2.5 2.5 1.7-1.7L5.7 7a4 4 0 0 1 4.5.8" stroke={color} strokeWidth={1.4} strokeLinecap="round" strokeLinejoin="round" />
    <path d="M10 14l-4 4M17 7l4 4" stroke={color} strokeWidth={1.3} strokeLinecap="round" opacity={0.6} />
    <path d="M8 18l-1.5 1.5M19 9l1.5-1.5" stroke={color} strokeWidth={1.8} strokeLinecap="round" />
  </svg>
);

/** Generic crafting — anvil + hammer */
export const CraftGenericIcon: React.FC<IconProps> = ({ size = 28, color = 'currentColor', className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <rect x="5" y="15" width="14" height="3.5" rx={1} fill={color} fillOpacity={0.4} stroke={color} strokeWidth={1} />
    <path d="M7 18.5v2M17 18.5v2" stroke={color} strokeWidth={1.2} strokeLinecap="round" />
    <rect x="10" y="6" width="4" height="3" rx={0.8} fill={color} />
    <line x1="12" y1="9" x2="12" y2="15" stroke={color} strokeWidth={1.5} strokeLinecap="round" />
  </svg>
);
